import React, { useState, useEffect } from "react";
import { questionsData } from "./NetZeroquestionData.js";
import "./ChecklistPage.css";
import { useAutoSave } from "../../hooks/useAutoSave";
import TextWithTooltip from "../../utils/TextWithTooltip.jsx";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import bannerImg from "../NetZerobanner.png";

const ChecklistPage = ({
  selectedClimateZone,
  onSave,
  builtUpArea,
  onChecklistChange,
  initialData = null,
  isEditMode = false,
  isAdminEdit = false,
  hotelDetails, // ✅ ADD THIS
  contactDetails,
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [otherInputs, setOtherInputs] = useState({});
  const [scores, setScores] = useState({});
  const [totalPoints, setTotalPoints] = useState(0);
  const [scopeAnswered, setScopeAnswered] = useState({});
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [activeScope, setActiveScope] = useState(null);
  const [showReadinessPopup, setShowReadinessPopup] = useState(false);
  const [showRequiredQuestionsPopup, setShowRequiredQuestionsPopup] =
    useState(false);
  const [popupSource, setPopupSource] = useState(null);
  const BANNER_URL = bannerImg;
  const userId = localStorage.getItem("User");
  const draftKey = `checklistDraft_${userId}`;
  useAutoSave({ selectedAnswers, otherInputs }, (dataToSave) => {
    console.log("Checklist Save Attempt. isAdminEdit = ", isAdminEdit);
    if (isAdminEdit) return;
    console.log("🚨 Checklist Draft SAVED - should NOT happen!");
    localStorage.setItem(draftKey, JSON.stringify(dataToSave));
  });

  useEffect(() => {
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        const { selectedAnswers: savedAnswers, otherInputs: savedOtherInputs } =
          JSON.parse(savedDraft);
        setSelectedAnswers(savedAnswers || {});
        setOtherInputs(savedOtherInputs || {});
      } catch (e) {
        console.warn("Failed to parse checklist draft from localStorage", e);
      }
    }
  }, [draftKey]);

  useEffect(() => {
    const q17Answer = selectedAnswers[17];

    // If Question 17 is answered "No", clear answers for 18, 19, 20
    if (q17Answer && q17Answer.label === "No") {
      setSelectedAnswers((prev) => {
        const updated = { ...prev };
        [18, 19, 20].forEach((qid) => delete updated[qid]);
        return updated;
      });
    }
  }, [selectedAnswers[17]]);

  const stripMarkup = (text) => {
    if (!text || typeof text !== "string") return text;

    //  Extract only the VISIBLE part of tooltip:
    // {{tooltip:PPA contract|long description}} → PPA contract
    text = text.replace(/\{\{tooltip:(.*?)\|.*?\}\}/g, "$1");

    text = text.replace(/\*\*/g, "");

    text = text.replace(/<[^>]+>/g, "");

    text = text.replace(/[ⓘℹ️🛈]/g, "");

    return text.trim();
  };

  //  Map API readinessResponses -> UI selectedAnswers (JSON format safe)
  const mapResponsesToAnswers = (responses) => {
    const answers = {};
    const otherInputsMap = {};

    (responses || []).forEach((resp) => {
      Object.values(questionsData).forEach((scope) => {
        (scope.categories || []).forEach((cat) => {
          (cat.questions || []).forEach((q) => {
            if (q.questionId === resp.questionId) {
              let parsedText = null;
              try {
                parsedText = JSON.parse(resp.questionText);
              } catch (e) {
                parsedText = resp.questionText;
              }

              if (resp.optionType === "single" || resp.optionType === "yesno") {
                const matched = (q.options || []).find(
                  (o) => o.label === parsedText
                );
                if (matched) answers[q.questionId] = matched;

                if (resp.otherText) {
                  otherInputsMap[q.questionId] = resp.otherText;
                }
              } else if (resp.optionType === "multi") {
                const selectedLabels = Array.isArray(parsedText)
                  ? parsedText
                  : String(parsedText || "")
                    .split(",")
                    .map((s) => s.trim());

                const matchedOpts = (q.options || []).filter((o) =>
                  selectedLabels.includes(o.label)
                );
                if (matchedOpts.length > 0) answers[q.questionId] = matchedOpts;

                if (resp.otherText) {
                  otherInputsMap[q.questionId] = resp.otherText;
                }
              }
            }
          });
        });
      });
    });

    return { answers, otherInputsMap };
  };

  // load initial data on edit
  useEffect(() => {
    if (initialData && isEditMode) {
      if (initialData.readinessResponses) {
        const { answers, otherInputsMap } = mapResponsesToAnswers(
          initialData.readinessResponses
        );
        setShowReadinessPopup(true);
        setSelectedAnswers(answers);
        setOtherInputs(otherInputsMap);
      }
      if (initialData.categoryScores) setScores(initialData.categoryScores);
      if (typeof initialData.totalPoints === "number")
        setTotalPoints(initialData.totalPoints);
    }
  }, [initialData, isEditMode]);

  // compute scores
  const computeScores = () => {
    const newScores = {};
    let newTotal = 0;

    Object.entries(questionsData).forEach(([scopeId, scope]) => {
      let scopePoints = 0;

      (scope.categories || []).forEach((category) => {
        (category.questions || []).forEach((q) => {
          const ans = selectedAnswers[q.questionId];
          if (!ans) return;

          if (Array.isArray(ans)) {
            ans.forEach((opt) => {
              if (opt && typeof opt.points === "number")
                scopePoints += opt.points;
            });
          } else if (typeof ans === "object" && ans.points !== undefined) {
            scopePoints += Number(ans.points) || 0;
          } else if (typeof ans === "number") {
            scopePoints += ans;
          }
        });
      });

      newScores[scopeId] = scopePoints;
      newTotal += scopePoints;
    });

    setScores(newScores);
    setTotalPoints(newTotal);
    return { newScores, newTotal };
  };

  const computeAnsweredCount = () => {
    const newAnswered = {};
    let total = 0;
    // Define question IDs to exclude
    const excludedIds = [7, 18, 19, 20];

    Object.entries(questionsData).forEach(([scopeId, scope]) => {
      let answeredCount = 0;

      (scope.categories || []).forEach((cat) => {
        (cat.questions || []).forEach((q) => {
          if (excludedIds.includes(q.questionId)) return;
          const ans = selectedAnswers[q.questionId];
          if (ans !== undefined && ans !== null) {
            if (Array.isArray(ans) && ans.length > 0) answeredCount++;
            else if (!Array.isArray(ans)) answeredCount++;
          }
        });
      });

      newAnswered[scopeId] = answeredCount;
      total += answeredCount;
    });

    setScopeAnswered(newAnswered);
    setTotalAnswered(total);
    return newAnswered;
  };

  useEffect(() => {
    computeScores();
    computeAnsweredCount();
  }, [selectedAnswers, otherInputs]);

  const areMandatoryQuestionsAnswered = () => {
    const mandatoryQuestions = [];
    Object.values(questionsData).forEach((scope) => {
      (scope.categories || []).forEach((cat) => {
        (cat.questions || []).forEach((q) => {
          if (q.mandatory === true) mandatoryQuestions.push(q.questionId);
        });
      });
    });

    if (mandatoryQuestions.length === 0) return true;

    for (const qId of mandatoryQuestions) {
      const ans = selectedAnswers[qId];
      if (!ans) return false;
      if (Array.isArray(ans)) {
        if (!ans.some((o) => o.label === "Yes")) return false;
      } else if (typeof ans === "object") {
        if (ans.label !== "Yes") return false;
      } else {
        if (ans !== true && ans !== "Yes") return false;
      }
    }
    return true;
  };

  // check if any answer exists
  const hasAnyAnswer = () => {
    for (const scope of Object.values(questionsData)) {
      for (const cat of scope.categories || []) {
        for (const q of cat.questions || []) {
          const ans = selectedAnswers[q.questionId];
          if (ans !== undefined && ans !== null) {
            if (Array.isArray(ans) && ans.length > 0) return true;
            if (!Array.isArray(ans)) return true;
          }
        }
      }
    }
    return false;
  };

  // handlers
  const handleSingleSelect = (questionId, option) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: option }));
  };

  const handleYesNo = (questionId, option) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: option }));
  };

  const handleMultiToggle = (questionId, option) => {
    setSelectedAnswers((prev) => {
      let prevArr = Array.isArray(prev[questionId])
        ? [...prev[questionId]]
        : [];
      const existsIndex = prevArr.findIndex((o) => o.label === option.label);
      if (existsIndex >= 0) prevArr.splice(existsIndex, 1);
      else prevArr.push(option);
      // Exclusive logic for Question 8
      if (questionId === 9) {
        const exclusiveLabel =
          "The hotel does not have a green building certification";
        const clickedIsExclusive = option.label === exclusiveLabel;

        if (clickedIsExclusive) {
          return { ...prev, [questionId]: [option] };
        } else {
          prevArr = prevArr.filter((o) => o.label !== exclusiveLabel);
          return { ...prev, [questionId]: prevArr };
        }
      }
      // QUESTION 10 EXCLUSIVE SELECTION
      if (questionId === 10) {
        const clean = (text) => stripMarkup(text).trim();

        const option1 = clean(
          "Hotel has bulk amenities dispensers in the guest bathroom, and recycles dispenser bottles"
        );
        const option2 = clean(
          "Hotel has bulk amenities dispensers in the guest bathroom, but does not yet recycle them"
        );

        const clicked = clean(option.label);

        if (clicked === option1) {
          prevArr = prevArr.filter((o) => clean(o.label) !== option2);
        }

        if (clicked === option2) {
          prevArr = prevArr.filter((o) => clean(o.label) !== option1);
        }

        return { ...prev, [questionId]: prevArr };
      }

      // Exclusive logic for Question 7
      if (questionId === 8) {
        const exclusiveLabel =
          "The hotel does not yet have any renewable energy system installed";
        const clickedIsExclusive = option.label === exclusiveLabel;

        if (clickedIsExclusive) {
          return { ...prev, [questionId]: [option] };
        } else {
          prevArr = prevArr.filter((o) => o.label !== exclusiveLabel);
          return { ...prev, [questionId]: prevArr };
        }
      }
      return { ...prev, [questionId]: prevArr };
    });
  };

  const handleOtherTextChange = (questionId, text) => {
    setOtherInputs((prev) => ({ ...prev, [questionId]: text }));
  };

  //  //  Mandatory questions check
  const mandatoryIds = [1, 5, 7]; // Q1, Q5, Q7
  const areMandatoryAnswered = () => {
    return mandatoryIds.every((id) => {
      const ans = selectedAnswers[id];
      if (!ans) return false;
      const label = Array.isArray(ans)
        ? ans.map((o) => stripMarkup(o.label))
        : stripMarkup(ans.label);
      if (id === 1)
        return [
          "Not applicable - The hotel does not use Fossil Fuels",
          "Fossil fuels used for both Space heating and Hot Water heating",
        ].some((opt) =>
          Array.isArray(label) ? label.includes(opt) : label === opt
        );
      if (id === 5)
        return [
          "Hotel has renewable electricity contract for 100% electrical annual consumption",
          "Hotel has a PPA contract for 100% renewable electricity",
          "Hotel purchases energy certificates (i.e. GOOs, RECs, IRECS) for 100% electical annual comsumption",
        ].some((l) => (Array.isArray(label) ? label.includes(l) : label === l));
      if (id === 7)
        return (
          label === "90% or more of the energy mix is from renewable sources"
        );
      return false;
    });
  };

  const getBottleQuestionId = () => {
    for (const scope of Object.values(questionsData)) {
      for (const cat of scope.categories || []) {
        for (const q of cat.questions || []) {
          if (
            q.options?.some((opt) =>
              stripMarkup(opt.label)
                .toLowerCase()
                .includes("tetra")
            )
          ) {
            return q.questionId; // ✅ REAL runtime ID
          }
        }
      }
    }
    return null;
  };

  const areQ9Q11Answered = () => {
    const q10 = selectedAnswers[10]; // amenities
    const bottleQId = getBottleQuestionId(); // ✅ real ID
    const qBottle = selectedAnswers[bottleQId];

    if (!q10 || !qBottle) return false;

    const normalize = (v) =>
      stripMarkup(v).replace(/\s+/g, " ").trim().toLowerCase();

    // ✅ STRICT allowed 3 ONLY
    const allowedBottleOptions = [
      (v) => v === "guestrooms offer glass bottles",
      (v) =>
        v.startsWith("guestrooms offer tetra") &&
        v.includes("cartons") &&
        v.includes("cans"),
      (v) => v === "guestrooms offer 100% plant-based bottle",
    ];

    const bottleLabels = Array.isArray(qBottle)
      ? qBottle.map((o) => normalize(o.label))
      : [normalize(qBottle.label)];

    const isBottleValid = allowedBottleOptions.some((rule) =>
      bottleLabels.some((label) => rule(label))
    );
    return Boolean(q10) && isBottleValid;
  };

  const questionNumberMap = {};

  Object.values(questionsData).forEach((scope) => {
    (scope.categories || []).forEach((cat) => {
      (cat.questions || []).forEach((q) => {
        const raw = q.text || "";
        const match = raw.match(/^(\d+(\.\d+)?)/); // extracts: 1, 5, 6.1, 7
        const number = match ? match[0] : q.questionId;
        questionNumberMap[q.questionId] = number;
      });
    });
  });

  const getMissingMandatoryQuestions = () => {
    const missing = [];

    mandatoryIds.forEach((id) => {
      const ans = selectedAnswers[id];
      const questionNumber = questionNumberMap[id] || id;

      if (!ans) {
        missing.push(questionNumber);
        return;
      }

      const label = Array.isArray(ans) ? ans.map((o) => o.label) : ans.label;

      if (id === 1) {
        const valid = [
          "Not applicable - The hotel does not use Fossil Fuels",
          "Fossil fuels used for both Space heating and Hot Water heating",
        ];
        if (
          !valid.some((v) =>
            Array.isArray(label) ? label.includes(v) : label === v
          )
        ) {
          missing.push(questionNumber);
        }
      }
      console.log("RAW ans:", ans);
      console.log("RAW LABEL:", ans.label);
      console.log("CLEANED LABEL:", label);
      console.log("LOWER:", label.toLowerCase());

      if (id === 5) {
        const valid = [
          "Hotel has renewable electricity contract for 100% electrical annual consumption",
          "Hotel has a PPA contract for 100% renewable electricity",
          "Hotel purchases energy certificates (i.e. GOOs, RECs, IRECS) for 100% electical annual comsumption",
        ];
        if (
          !valid.some((v) =>
            Array.isArray(label) ? label.includes(v) : label === v
          )
        ) {
          missing.push(questionNumber);
        }
      }

      if (id === 7) {
        if (
          label !== "90% or more of the energy mix is from renewable sources"
        ) {
          missing.push(questionNumber);
        }
      }
    });

    return missing;
  };

  const getReadinessLevel = (points) => {
    const NOT_READY_MSG =
      "Let’s work together on Scope 3! Click here to know what systems you can adapt in your hotel and how to implement them.";

    const clean = (t) => stripMarkup(t).trim().toLowerCase();

    if (!areMandatoryAnswered()) {
      const missing = getMissingMandatoryQuestions();

      return {
        level: "Not ready",
        message: `Mandatory criteria not met. Revisit question: ${missing.join(", ")}`,
      };
    }

    //  RULE A — Q10: Mini amenities → ALWAYS NOT READY
    const q10 = selectedAnswers[10];

    if (q10 && Array.isArray(q10)) {
      const miniAmenityLabel = clean(
        "Hotel has mini amenities in the guest bathroom"
      );

      const hasMiniAmenities = q10.some(
        (o) => clean(o.label) === miniAmenityLabel
      );

      if (hasMiniAmenities) {
        return { level: "Not ready", message: NOT_READY_MSG };
      }
    }
    //Bottle selection must be Option 1, 2, or 3 ONLY
    const bottleQId = getBottleQuestionId();
    const qBottle = selectedAnswers[bottleQId];

    const q12Preferred = [
      "guestrooms offer glass bottles",
      "guestrooms offer tetra pack cartons or cans",
      "guestrooms offer 100% plant-based bottle",
    ];

    if (!qBottle) {
      return { level: "Not ready", message: NOT_READY_MSG };
    }

    const qBottleLabels = Array.isArray(qBottle)
      ? qBottle.map((o) =>
        stripMarkup(o.label).replace(/\s+/g, " ").trim().toLowerCase()
      )
      : [
        stripMarkup(qBottle.label)
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase(),
      ];

    const validQBottle = q12Preferred
      .map((v) => v.replace(/\s+/g, " ").trim().toLowerCase())
      .some((opt) => qBottleLabels.includes(opt));

    if (!validQBottle) {
      return { level: "Not ready", message: NOT_READY_MSG };
    }


    const linkText = "Verified Net Zero hotels: Systems for Scope 3 reduction";
    const linkUrl =
      "https://login.microsoftonline.com/1e69e311-af1f-4162-b24e-16bb3f962d34/oauth2/authorize?...";

    let level = "";
    let message = "";
    let pdfMessage = "";

    if (points >= 10) {
      if (areQ9Q11Answered()) {
        level = "High Readiness";
        message = (
          <>
            <strong style={{ fontSize: "17px", display: "inline-block" }}>
              Congratulations!
            </strong>
            <br />
            <br />
            A member of the RB team will be in touch with you shortly. Email{" "}
            <a
              href="mailto:responsiblebusiness@radissonhotels.com"
              style={{ color: "#007bff", textDecoration: "underline" }}
            >
              responsiblebusiness@radissonhotels.com
            </a>{" "}
            to share the great news!<br />
          </>
        );
        pdfMessage =
          "Congratulations! A member of the RB team will be in touch with you shortly. Email responsiblebusiness@radissonhotels.com to share the great news!";
      } else {
        level = "Halfway there!";
        message =
          "Even though your score is high, please ensure both Q9 and Q11 are completed with valid options to achieve High Readiness.";
        pdfMessage = message;
      }
    } else if (points >= 5) {
      level = "Halfway there!";
      message =
        "The hotel has implemented some of the required systems. Explore other systems you can implement to increase your score and reduce operational emissions here.";
      pdfMessage = message;
    } else {
      level = "Not ready";
      message =
        "Let’s work together on Scope 3! Click here to know what systems you can adapt in your hotel and how to implement them.";
      pdfMessage = message;
    }

    return { level, message, pdfMessage, linkText, linkUrl };
  };

  const getScope1and2Readiness = (points) => {
    if (!areMandatoryAnswered()) {
      const missing = getMissingMandatoryQuestions();

      return {
        level: "Not ready",
        message: `Mandatory criteria not met. Revisit question: ${missing.join(", ")}`,
      };
    }

    const linkText = "Verified Net Zero hotels: Systems for Scope 3 reduction";
    const linkUrl =
      "https://login.microsoftonline.com/1e69e311-af1f-4162-b24e-16bb3f962d34/oauth2/authorize?...";

    let level = "";
    let message = "";

    if (points >= 10) {
      level = "Halfway there!";
      message =
        "The hotel has implemented some of the required systems. Explore other systems you can implement to increase your score and reduce operational emissions here.";
    } else if (points >= 5) {
      level = "Halfway there!";
      message =
        "The hotel has implemented some of the required systems. Explore other systems you can implement to increase your score and reduce operational emissions here.";
    } else {
      level = "Not ready";
      message =
        "Let’s work together on Scope 3! Click here to know what systems you can adapt in your hotel and how to implement them.";
    }

    return { level, message, linkText, linkUrl };
  };

  const handleCredibilityCheck = () => {
    setPopupSource("overall");
    setShowReadinessPopup(true);
  };

  const handleSave = () => {
    if (!hasAnyAnswer()) {
      setShowRequiredQuestionsPopup(true);
      return;
    }
    if (!areMandatoryQuestionsAnswered()) {
      setShowReadinessPopup(true);
      return;
    }

    const readiness = getReadinessLevel(totalPoints);

    // Build readinessResponses
    const readinessResponses = [];
    Object.values(questionsData).forEach((scope) => {
      (scope.categories || []).forEach((cat) => {
        (cat.questions || []).forEach((q) => {
          const ans = selectedAnswers[q.questionId];
          const answered =
            ans !== undefined &&
            ans !== null &&
            ((Array.isArray(ans) && ans.length > 0) || !Array.isArray(ans));

          let points = 0;
          let questionText = "";
          let otherText = "";

          if (answered) {
            if (Array.isArray(ans)) {
              questionText = JSON.stringify(
                ans.map((o) => stripMarkup(o.label))
              );
              ans.forEach((opt) => {
                if (opt && typeof opt.points === "number") points += opt.points;
              });

              const otherOpt = ans.find((o) => o.other);
              if (otherOpt && otherInputs[q.questionId]) {
                otherText = otherInputs[q.questionId];
              }
            } else if (typeof ans === "object" && ans.points !== undefined) {
              points += Number(ans.points) || 0;
              questionText = JSON.stringify(stripMarkup(ans.label));

              if (ans.other && otherInputs[q.questionId]) {
                otherText = otherInputs[q.questionId];
              }
            } else {
              questionText = JSON.stringify(stripMarkup(String(ans)));
            }
          }

          readinessResponses.push({
            questionId: q.questionId,
            userId: userId ? parseInt(userId, 10) : null,
            answer: answered,
            pointsAchieved: points,
            optionType: q.type,
            questionText: questionText,
            otherText: otherText,
          });
        });
      });
    });

    const payload = {
      userId: userId ? parseInt(userId, 10) : null,
      totalPoints,
      totalAnswered,
      readinessLevels: readiness.level,
      readinessResponses,
      categoryScores: scores,
      selectedAnswers,
      otherInputs,
      CalculatedReadinessLevel: readiness.level,
      ReadinessMessage: readiness.message,
    };

    if (typeof onSave === "function") onSave(payload);
  };

  const COLOR_MAIN = rgb(0x72 / 255, 0xcb / 255, 0xb2 / 255); // #72CBB2
  const COLOR_SCOPE = rgb(0x00 / 255, 0x5c / 255, 0x7d / 255); // #005C7D
  const COLOR_BLACK = rgb(0, 0, 0);
  const COLOR_RESULT_NOT_READY = rgb(0x72 / 255, 0x1c / 255, 0x24 / 255); // #721c24
  const COLOR_RESULT_READY = rgb(0x15 / 255, 0x57 / 255, 0x24 / 255); // #155724
  const COLOR_RESULT_OTHER = rgb(0x85 / 255, 0x64 / 255, 0x04 / 255); // #856404

  const getResultColor = (level) => {
    const val = String(level || "").toLowerCase();

    if (val.includes("not ready")) return COLOR_RESULT_NOT_READY;
    if (val.includes("high")) return COLOR_RESULT_READY;
    if (val.includes("ready")) return COLOR_RESULT_READY;
    if (val.includes("readiness")) return COLOR_RESULT_READY;

    return COLOR_RESULT_OTHER;
  };
  const shouldShowQuestionInPDF = (qId) => {
    let shouldShow = true;

    // ✅ Q18,19,20 depend on Q17
    if ([18, 19, 20].includes(qId)) {
      const q17Answer = selectedAnswers[17];
      if (q17Answer && q17Answer.label === "No") {
        shouldShow = false;
      }
    }

    // ✅ Q7 depends on Q6
    if (qId === 7) {
      const q6Answer = selectedAnswers[6];
      if (q6Answer) {
        if (Array.isArray(q6Answer)) {
          if (
            q6Answer.some((opt) =>
              opt.label.includes(
                "Not applicable: The hotel does not purchase neither District Heating and/or Cooling"
              )
            )
          ) {
            shouldShow = false;
          }
        } else if (
          q6Answer.label.includes(
            "Not applicable: The hotel does not purchase neither District Heating and/or Cooling"
          )
        ) {
          shouldShow = false;
        }
      } else {
        shouldShow = false;
      }
    }

    return shouldShow;
  };

  const handleDownloadPDF = async () => {
    const readiness =
      popupSource === "scope2"
        ? getScope1and2Readiness(totalPoints)
        : getReadinessLevel(totalPoints);

    const pdfDoc = await PDFDocument.create();
    let page = pdfDoc.addPage([595, 842]); // A4
    const { width, height } = page.getSize();

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // ✅ MARGINS
    const margin = {
      top: 40,
      bottom: 50,
      left: 50,
      right: 50,
    };

    let y = height - margin.top;
    const LINE_GAP = 6; // normal text spacing
    const SECTION_GAP = 14; // space after section headers
    const QUESTION_GAP = 10;

    const newPage = () => {
      page = pdfDoc.addPage([595, 842]);
      y = height - margin.top;
    };

    const wrapText = (text, maxWidth, fontSize) => {
      const words = String(text || "").split(" ");
      let lines = [];
      let line = "";

      words.forEach((word) => {
        const testLine = line + word + " ";
        const w = font.widthOfTextAtSize(testLine, fontSize);

        if (w > maxWidth) {
          lines.push(line.trim());
          line = word + " ";
        } else {
          line = testLine;
        }
      });

      lines.push(line.trim());
      return lines;
    };

    const drawText = (text, size = 11, bold = false, color = COLOR_BLACK) => {
      const activeFont = bold ? boldFont : font;
      const lines = wrapText(text, width - margin.left - margin.right, size);

      lines.forEach((line) => {
        if (y < margin.bottom) newPage();

        page.drawText(line, {
          x: margin.left,
          y,
          size,
          font: activeFont,
          color,
        });

        y -= size + LINE_GAP;
      });
    };

    // BANNER WITH SIDE & TOP MARGINS
    try {
      const imgBytes = await fetch(BANNER_URL).then((res) => res.arrayBuffer());
      const banner = await pdfDoc.embedPng(imgBytes);

      const bannerHeight = 110;

      page.drawImage(banner, {
        x: margin.left,
        y: height - bannerHeight - margin.top,
        width: width - margin.left - margin.right,
        height: bannerHeight,
      });

      y = height - bannerHeight - margin.top - 30;
    } catch (err) {
      console.warn("Banner not loaded:", err);
    }

    // ✅ Load checkbox images
    const loadImage = async (url) => {
      const res = await fetch(url);
      const bytes = await res.arrayBuffer();

      const lower = url.toLowerCase();

      if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) {
        return pdfDoc.embedJpg(bytes);
      }

      // ✅ Try PNG, fallback to JPG automatically
      try {
        return pdfDoc.embedPng(bytes);
      } catch (e) {
        return pdfDoc.embedJpg(bytes);
      }
    };

    // ✅ Load both images safely
    const checkImage = await loadImage("/check.png");
    const xBoxImage = await loadImage("/Box.png");

    const drawOptionWithTick = (label, isChecked) => {
      const boxSize = 10;
      const gap = 10;
      const textX = margin.left + boxSize + gap;
      const maxWidth = width - textX - margin.right;
      const fontSize = 11; // ✅ DEFINE SIZE HERE

      if (y < margin.bottom) newPage();

      const icon = isChecked ? checkImage : xBoxImage;

      // ✅ Draw checkbox
      page.drawImage(icon, {
        x: margin.left,
        y: y - boxSize + 9,
        width: boxSize,
        height: boxSize,
      });

      // ✅ Wrapped option text
      const lines = wrapText(stripMarkup(label), maxWidth, fontSize);

      lines.forEach((line) => {
        if (y < margin.bottom) newPage();

        page.drawText(line, {
          x: textX,
          y,
          size: fontSize,
          font,
          color: COLOR_BLACK,
        });

        y -= fontSize + LINE_GAP; // ✅ FIXED: no more undefined `size`
      });
    };

    const drawCenteredText = (
      text,
      size = 12,
      bold = false,
      color = COLOR_BLACK
    ) => {
      const activeFont = bold ? boldFont : font;
      const textWidth = activeFont.widthOfTextAtSize(text, size);

      const x = (width - textWidth) / 2; // ✅ Perfect horizontal centering

      if (y < margin.bottom) newPage();

      page.drawText(text, {
        x,
        y,
        size,
        font: activeFont,
        color,
      });

      y -= size + 10;
    };
    const drawCenteredWrappedText = (
      text,
      fontSize = 11,
      bold = false,
      color = COLOR_BLACK
    ) => {
      const activeFont = bold ? boldFont : font;
      const maxWidth = width - margin.left - margin.right - 120;
      const lines = wrapText(text, maxWidth, fontSize);

      lines.forEach((line) => {
        if (y < margin.bottom) newPage();

        const textWidth = activeFont.widthOfTextAtSize(line, fontSize);
        const x = (width - textWidth) / 2;

        page.drawText(line, {
          x,
          y,
          size: fontSize,
          font: activeFont,
          color,
        });

        y -= fontSize + LINE_GAP;
      });
    };

    const col4 = (width - margin.left - margin.right) / 4;
    const col3 = (width - margin.left - margin.right) / 3;

    drawText("Hotel Details", 14, true, COLOR_MAIN);
    y -= 6;

    // HEADERS
    page.drawText("Hotel Name", {
      x: margin.left + col4 * 0,
      y,
      size: 11,
      font: boldFont,
    });
    page.drawText("SITA Code", {
      x: margin.left + col4 * 1,
      y,
      size: 11,
      font: boldFont,
    });
    page.drawText("Country", {
      x: margin.left + col4 * 2,
      y,
      size: 11,
      font: boldFont,
    });
    page.drawText("Contract Type", {
      x: margin.left + col4 * 3,
      y,
      size: 11,
      font: boldFont,
    });

    y -= 14;

    // VALUES
    page.drawText(hotelDetails?.hotelName || "-", {
      x: margin.left + col4 * 0,
      y,
      size: 11,
      font,
    });
    page.drawText(hotelDetails?.sitaCode || "-", {
      x: margin.left + col4 * 1,
      y,
      size: 11,
      font,
    });
    page.drawText(hotelDetails?.country || "-", {
      x: margin.left + col4 * 2,
      y,
      size: 11,
      font,
    });
    page.drawText(hotelDetails?.contractType || "-", {
      x: margin.left + col4 * 3,
      y,
      size: 11,
      font,
    });

    y -= SECTION_GAP * 2;

    drawText("Contact Details", 14, true, COLOR_MAIN);
    y -= 6;

    // HEADERS
    page.drawText("Name", {
      x: margin.left + col3 * 0,
      y,
      size: 11,
      font: boldFont,
    });
    page.drawText("Job Title", {
      x: margin.left + col3 * 1,
      y,
      size: 11,
      font: boldFont,
    });
    page.drawText("RHG Email", {
      x: margin.left + col3 * 2,
      y,
      size: 11,
      font: boldFont,
    });

    y -= 14;

    // VALUES
    page.drawText(contactDetails?.name || "-", {
      x: margin.left + col3 * 0,
      y,
      size: 11,
      font,
    });
    page.drawText(contactDetails?.jobTitle || "-", {
      x: margin.left + col3 * 1,
      y,
      size: 11,
      font,
    });
    page.drawText(contactDetails?.email || "-", {
      x: margin.left + col3 * 2,
      y,
      size: 11,
      font,
    });

    y -= SECTION_GAP * 2;
    y -= 30;
    // ✅ ✅ ✅ 4. QUESTIONS WITH SCOPE HEADERS
    drawText("Questions Answered", 15, true, COLOR_MAIN);
    y -= SECTION_GAP;

    Object.entries(questionsData).forEach(([scopeId, scope]) => {
      // ✅ SCOPE HEADER
      drawText(scope.name, 14, true, COLOR_SCOPE);
      y -= 8;

      (scope.categories || []).forEach((cat) => {
        // ✅ CATEGORY HEADER (WHAT YOU WERE MISSING)
        drawText(stripMarkup(cat.categoryName), 12, true, COLOR_SCOPE);
        y -= 6;

        (cat.questions || []).forEach((q) => {
          const shouldShow = shouldShowQuestionInPDF(q.questionId);
          if (!shouldShow) return;
          const ans = selectedAnswers[q.questionId];
          const selectedLabels = Array.isArray(ans)
            ? ans.map((o) => o.label)
            : ans?.label
              ? [ans.label]
              : [];

          const questionText = stripMarkup(q.text);

          // ✅ QUESTION TEXT
          drawText(`${questionText}`, 11, true);
          y -= QUESTION_GAP;

          // ✅ ALL OPTIONS WITH ✅ ON SELECTED
          (q.options || []).forEach((opt) => {
            const isChecked = selectedLabels.includes(opt.label);

            let finalLabel = stripMarkup(opt.label);

            // ✅ APPEND OTHER INPUT INLINE IF THIS OPTION HAS IT
            if (isChecked && opt.other && otherInputs[q.questionId]) {
              finalLabel = `${finalLabel} (${otherInputs[q.questionId]})`;
            }

            drawOptionWithTick(finalLabel, isChecked);
          });

          y -= QUESTION_GAP;
        });
      });

      y -= SECTION_GAP;
    });

    newPage();
    drawCenteredText("Net Zero Readiness Result", 18, true, COLOR_SCOPE);

    const stateColor = getResultColor(readiness.level);

    drawCenteredText("Readiness Level", 12, true, COLOR_BLACK);
    drawCenteredText(readiness.level, 14, true, stateColor);

    drawCenteredText(
      `Total Answered Questions: ${totalAnswered}/17`,
      12,
      true,
      COLOR_BLACK
    );
    y -= 6;
    const normalizePdfText = (text) => {
      if (!text) return "";
      return String(text)
        .replace(/\n+/g, " ")   // ❌ remove newlines
        .replace(/\s+/g, " ")  // ✅ normalize spaces
        .trim();
    };
    drawCenteredText("Result Message", 12, true, COLOR_BLACK);
    y -= 6;
    drawCenteredWrappedText(
      normalizePdfText(
        typeof readiness === "object" && "pdfMessage" in readiness
          ? readiness.pdfMessage
          : readiness.message
      ),
      11,
      false,
      stateColor
    );
    // SAVE PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "NetZero_Readiness_Report.pdf";
    link.click();
  };

  return (
    <div className="checklist-container">
      <div className="total-points">
        <span>QUESTIONS ANSWERED</span>
        <span className="points-box">{totalAnswered}/17</span>
      </div>

      {Object.entries(questionsData).map(([scopeId, scope]) => (
        <div key={scopeId} className="scope">
          <div
            className={`scope-header ${activeScope === scopeId ? "active" : ""
              }`}
            onClick={() =>
              setActiveScope(activeScope === scopeId ? null : scopeId)
            }
          >
            <span>
              {scope.icon} {scope.name}
            </span>
            <div className="scope-header-right">
              <span className="scope-score">
                {scopeAnswered[scopeId] || 0}/{scope.maxQuestions}
              </span>
              <span className="scope-arraow">▼</span>
            </div>
          </div>

          <div
            className={`scope-content ${activeScope === scopeId ? "active" : ""
              }`}
          >
            {(scope.categories || []).map((cat, catIdx) => (
              <div
                key={cat.categoryId || `${scopeId}-cat-${catIdx}`}
                className="category-block"
              >
                <h4 className="category-name">
                  <TextWithTooltip text={cat.categoryName} />
                </h4>

                {(cat.questions || []).map((q) => {
                  const qId = q.questionId;
                  const answer = selectedAnswers[qId];
                  // Conditional display for 6.1
                  let shouldShow = true;
                  if ([18, 19, 20].includes(qId)) {
                    const q17Answer = selectedAnswers[17];
                    if (q17Answer && q17Answer.label === "No") {
                      shouldShow = false;
                    }
                  }
                  if (qId === 7) {
                    const q6Answer = selectedAnswers[6];
                    if (q6Answer) {
                      if (Array.isArray(q6Answer)) {
                        if (
                          q6Answer.some((opt) =>
                            opt.label.includes(
                              "Not applicable: The hotel does not purchase neither District Heating and/or Cooling"
                            )
                          )
                        ) {
                          shouldShow = false;
                        }
                      } else if (
                        q6Answer.label.includes(
                          "Not applicable: The hotel does not purchase neither District Heating and/or Cooling"
                        )
                      ) {
                        shouldShow = false;
                      }
                    } else {
                      shouldShow = false;
                    }
                  }
                  if (!shouldShow) return null;
                  return (
                    <div key={qId} className="question">
                      <p className="question-text">
                        <TextWithTooltip text={q.text} />
                        {q.mandatory && (
                          <span className="mandatory-asterisk"> *</span>
                        )}
                      </p>
                      {q.helperText && (
                        <p className="helper-text">{q.helperText}</p>
                      )}

                      <div className="answer-area">
                        {q.type === "single" &&
                          q.options &&
                          q.options.map((opt, idx) => {
                            const isSelected =
                              answer && answer.label === opt.label;
                            return (
                              <div key={idx} className="option-with-other">
                                <label className="option-label">
                                  <input
                                    type="radio"
                                    name={`q-${qId}`}
                                    checked={isSelected}
                                    onChange={() =>
                                      handleSingleSelect(qId, opt)
                                    }
                                  />
                                  <span>
                                    {" "}
                                    <TextWithTooltip text={opt.label} />
                                  </span>
                                </label>

                                {isSelected && opt.other && (
                                  <input
                                    className="other-input"
                                    placeholder={
                                      qId === 2
                                        ? "Indicate refrigerant used"
                                        : "Please specify"
                                    }
                                    value={otherInputs[qId] || ""}
                                    onChange={(e) =>
                                      handleOtherTextChange(qId, e.target.value)
                                    }
                                  />
                                )}
                              </div>
                            );
                          })}

                        {q.type === "yesno" && (
                          <div className="answer-area yesno">
                            {q.options &&
                              q.options.map((opt, idx) => (
                                <button
                                  key={idx}
                                  className={`yn-btn ${answer && answer.label === opt.label
                                      ? "selected"
                                      : ""
                                    }`}
                                  onClick={() => handleYesNo(qId, opt)}
                                >
                                  {opt.label}
                                </button>
                              ))}
                          </div>
                        )}

                        {q.type === "multi" &&
                          q.options &&
                          q.options.map((opt, idx) => {
                            const checked =
                              Array.isArray(answer) &&
                              answer.some((a) => a.label === opt.label);

                            const isOtherSelected =
                              opt.other &&
                              Array.isArray(answer) &&
                              answer.some(
                                (a) => a.label === opt.label && a.other
                              );

                            return (
                              <div key={idx} className="option-with-other">
                                <label className="option-label">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => handleMultiToggle(qId, opt)}
                                  />
                                  <span>
                                    <TextWithTooltip text={opt.label} />
                                  </span>
                                </label>

                                {isOtherSelected && (
                                  <input
                                    className="other-input"
                                    placeholder={
                                      qId === 2
                                        ? "Indicate refrigerant used"
                                        : "Please specify"
                                    }
                                    value={otherInputs[qId] || ""}
                                    onChange={(e) =>
                                      handleOtherTextChange(qId, e.target.value)
                                    }
                                  />
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {scopeId === "2" && (
            <div className="scope-button-wrapper">
              <button
                className="check-scope-btn-inline"
                onClick={(e) => {
                  e.stopPropagation();
                  setPopupSource("scope2");
                  setShowReadinessPopup(true);
                }}
              >
                Check Scope 1 & 2 Readiness
              </button>
            </div>
          )}
        </div>
      ))}

      <div className="buttons">
        <button className="save-btn" onClick={handleSave}>
          Save
        </button>
        <button className="credibility-btn" onClick={handleCredibilityCheck}>
          Overall Readiness ↗️
        </button>
      </div>

      {showReadinessPopup &&
        (() => {
          const readiness =
            popupSource === "scope2"
              ? getScope1and2Readiness(totalPoints)
              : getReadinessLevel(totalPoints);

          return (
            <>
              <div
                className="popup-overlay"
                onClick={() => setShowReadinessPopup(false)}
              />
              <div className="readiness-popup">
                <h3 className="popup-title">
                  {popupSource === "scope2"
                    ? "Scope 1 & 2 Readiness"
                    : "Net Zero Readiness"}
                </h3>

                <div
                  className={`result-box ${readiness.level
                    .replace(/\s+/g, "-")
                    .toLowerCase()}`}
                >
                  <h2 className="result-text">{readiness.level}</h2>

                  <p className="result-msg">
                    {readiness.message}{" "}
                    {readiness.linkText && readiness.linkUrl && (
                      <a
                        href={readiness.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: "underline",
                          color: "#007bff",
                        }}
                      >
                        {readiness.linkText}
                      </a>
                    )}
                  </p>
                </div>

                <div className="points-box">
                  <p className="points-text">
                    Questions Answered:{" "}
                    <span>
                      {popupSource === "scope2"
                        ? Object.keys(selectedAnswers).filter((id) => {
                          const qid = parseInt(id);
                          return (
                            qid <= 9 && //  Only count up to 9
                            qid !== 7 && //  Exclude question 7
                            selectedAnswers[id] !== undefined &&
                            selectedAnswers[id] !== null &&
                            (Array.isArray(selectedAnswers[id])
                              ? selectedAnswers[id].length > 0
                              : true)
                          );
                        }).length
                        : totalAnswered}
                      /{popupSource === "scope2" ? 8 : 17}
                    </span>
                  </p>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                  }}
                >
                  {popupSource !== "scope2" && (
                    <button className="close-popup" onClick={handleDownloadPDF}>
                      Download PDF
                    </button>
                  )}

                  <button
                    className="close-popup"
                    onClick={() => setShowReadinessPopup(false)}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </>
          );
        })()}

      {showRequiredQuestionsPopup && (
        <>
          <div
            className="popup-overlay"
            onClick={() => setShowRequiredQuestionsPopup(false)}
          />
          <div className="readiness-popup">
            <h3>Please answer questions</h3>
            <p>Please provide an answer before saving.</p>
            <button
              className="close-popup"
              onClick={() => setShowRequiredQuestionsPopup(false)}
            >
              Continue
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(ChecklistPage);
